/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import VideoModal from '../components/VideoModal';
import HistoryButton from '../components/HistoryButton';
import HistoryModal from '../components/HistoryModal';
import Step8ListModal from '../components/Step8ListModal';
import { progressService } from '../services/progressService';
import { InventoryService } from '../services/inventoryService';
import { useDialog } from '../context/DialogContext';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';

interface Step8Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step8: React.FC<Step8Props> = ({ user, onLogin, onLogout }) => {
  const { showAlert } = useDialog();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isListModalOpen, setIsListModalOpen] = useState(false);
  const [listCount, setListCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: "Read the SAA Green Book about Step Eight",
      description: "Learn why naming whom I've harmed and building willingness are critical before making amends",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: "How to work Step Eight (guidance & video)",
      description: "Read guidance and watch video on how to work through Step Eight",
      completed: false,
      buttons: [
        {
          text: "Read Guidance",
          action: () => setIsGuidanceOpen(true),
          variant: 'primary'
        },
        {
          text: "Watch Video",
          action: () => setIsVideoModalOpen(true),
          variant: 'secondary'
        }
      ]
    },
    {
      id: 3,
      title: "Take Step Eight: Make a list of persons I have harmed",
      description: "Create a list of people you have harmed through your actions",
      completed: false,
      hasButton: true,
      buttonText: "Open Amends List",
      buttonAction: () => setIsListModalOpen(true)
    },
    {
      id: 4,
      title: "Review my completed list with my sponsor",
      description: "Share your Step Eight list with your sponsor and discuss your willingness to make amends",
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step Eight', path: '/saa/12-steps/step-8', active: true }
  ];

  // Check if a person entry is complete (same logic as Step8ListModal)
  const isPersonComplete = (person: any): boolean => {
    if (!person || typeof person !== 'object') return false;
    
    const name = (person.name && typeof person.name === 'string') ? person.name.trim() : '';
    const note = (person.note && typeof person.note === 'string') ? person.note.trim() : '';
    const timing = (person.timing && typeof person.timing === 'string') ? person.timing.trim() : '';
    const form = (person.form && typeof person.form === 'string') ? person.form.trim() : '';
    
    return !!(
      name.length > 0 && 
      note.length > 0 &&
      timing.length > 0 &&
      form.length > 0
    );
  };

  // Load list count and completion status
  const loadListCount = async () => {
    if (!user) {
      setListCount(0);
      setCompletedCount(0);
      return;
    }
    
    try {
      const items = await InventoryService.getInventory('step8-persons', 'default');
      // Filter and map items the same way as Step8ListModal does
      const personItems = items
        .filter(item => item != null && typeof item === 'object' && item._type !== 'completion_status')
        .map(item => ({
          name: item.name || '',
          note: item.note || '',
          timing: item.timing || undefined,
          form: item.form || undefined
        }));
      setListCount(personItems.length);
      
      // Count completed entries using the same logic
      const completed = personItems.filter(item => isPersonComplete(item)).length;
      setCompletedCount(completed);
    } catch (error) {
      // If no list exists yet, counts are 0
      setListCount(0);
      setCompletedCount(0);
    }
  };

  // Initialize progress tracking
  const initializeProgress = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }
    
    setIsInitializing(true);
    
    try {
      // Initialize SAA progress
      await progressService.initializeProgress();
      
      // Load list count
      await loadListCount();
      
      // Fetch progress data
      const existingProgress = await progressService.getStepProgress('SAA', '12-steps', 8).catch(() => null);
      
      if (existingProgress) {
        const savedData = existingProgress.data || {};
        setStepData(existingProgress);
        
        // Restore actions state
        if (savedData.actions) {
          // Merge saved actions with original actions to restore missing properties
          const mergedActions = originalActions.map(originalAction => {
            const savedAction = savedData.actions.find((a: any) => a.id === originalAction.id);
            if (savedAction) {
              return {
                ...originalAction,
                completed: savedAction.completed,
                description: savedAction.description || originalAction.description
              };
            }
            return originalAction;
          });
          setActions(mergedActions);
        }
      }
      
      // Check date tracking preference
      try {
        const journeyData = await progressService.getJourneyProgress();
        setIsOptedOut(journeyData.opted_out);
      } catch (error) {
        console.error('Error loading date tracking preference:', error);
      }
    } catch (err: any) {
      console.error('Error initializing progress:', err);
      handleError(err, 'Failed to load your progress. Please try refreshing the page.');
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    initializeProgress();
  }, [user]);

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const isActionAvailable = (actionIndex: number) => {
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  // Validate action 3 completion - requires at least 1 completed person entry
  const validateAction3Completion = () => {
    return completedCount > 0;
  };

  // Check if action 3 can be completed (list must have at least one completed person)
  const canCompleteAction3 = () => {
    return validateAction3Completion();
  };

  // Check if any action can be completed
  const canCompleteAction = (actionId: number) => {
    if (actionId === 3) {
      return canCompleteAction3();
    }
    if (actionId === 4) {
      // Action 4 requires Action 3 to be completed
      const action3 = actions.find(a => a.id === 3);
      return action3 ? action3.completed : false;
    }
    return true; // Actions 1 and 2 can always be completed
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    // Check if action 3 is being marked complete
    const action3 = updatedActions.find(a => a.id === 3);
    const currentAction3 = actions.find(a => a.id === 3);
    
    if (action3 && !currentAction3?.completed && action3.completed) {
      // Action 3 is being marked complete - validate
      if (!validateAction3Completion()) {
        showAlert({
          message: 'Please complete at least one person entry (name, note, timing, and form) before marking this action complete.',
          variant: 'warning'
        });
        return;
      }
    }
    
    // For other actions, save to backend
    if (!user) {
      handleError(new Error('User not logged in'), 'Please log in to save your progress.');
      return;
    }
    
    try {
      // Update local state
      setActions(updatedActions);
      
      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 8,
        step_title: 'Step Eight',
        completed: allCompleted,
        data: {
          actions: updatedActions
        }
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }
      
      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Eight - ${completedCount} of 4 actions completed`,
        progress_milestone: allCompleted ? 'step8_completed' : 'step8_in_progress'
      });
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, 'Failed to save progress. Please try again.');
    }
  };

  // Check if all actions are completed
  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>Step Eight</h1>
            {stepData && (
              <HistoryButton
                stepNumber={8}
                stepTitle="Step Eight"
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>
            Made a list of all persons we had harmed, and became willing to make amends to them all.
          </p>
        </div>

        {isInitializing && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
              </>
            )}
          </div>
        )}

        {!user && (
          <StepLoginPrompt onLogin={onLogin} />
        )}

        {!isInitializing && !errorMessage && user && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>Actions to Complete Step Eight</h2>
            <p className={styles.actionsDescription}>
              Complete each action in order. Check the box when you've finished each action.
            </p>
            <p style={{ marginTop: '0.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
              Action 4 requires Action 3 to be completed first.
            </p>
            
            <StepActions
              actions={actions}
              onActionChange={handleActionChange}
              stepNumber={8}
              canCompleteAction={canCompleteAction}
              renderActionContent={(action, index) => {
                if (action.id === 3) {
                  return (
                    <div style={{ 
                      marginTop: '0.5rem', 
                      color: '#6b7280', 
                      fontSize: '0.875rem',
                      paddingLeft: '56px' // Align with button: actionNumber (32px) + margin-right (0.75rem ≈ 12px) + gap (0.75rem ≈ 12px) = 56px
                    }}>
                      {listCount === 0 ? (
                        <span>No persons added yet. Click "Open Amends List" to get started.</span>
                      ) : (
                        <span>{listCount} {listCount === 1 ? 'person' : 'persons'} on your list ({completedCount} {completedCount === 1 ? 'completed' : 'completed'}).</span>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />

            {allCompleted && (
              <>
                <div className={styles.completionMessage}>
                  <h3>🎉 Congratulations!</h3>
                  <p>You have completed all actions for Step Eight and can move on to Step Nine.</p>
                </div>
                <div className={styles.stepNavigation}>
                  <div className={styles.navigationLinks}>
                    <a href="/saa/12-steps/step-7" className={styles.prevStepLink}>
                      {'<<'} Step Seven
                    </a>
                    <span></span>
                    <a href="/saa/12-steps/step-9" className={styles.nextStepLink}>
                      Step Nine {'>>'}
                    </a>
                  </div>
                </div>
              </>
            )}

            {!allCompleted && (
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                  <a href="/saa/12-steps/step-7" className={styles.prevStepLink}>
                    {'<<'} Step Seven
                  </a>
                  <span></span>
                  <a href="/saa/12-steps/step-9" className={styles.nextStepLink}>
                    Step Nine {'>>'}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-eight start>>"
        endTag="<<step-eight end>>"
      />

      {/* ContentReader for How to Work Step Eight */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_8_Guidance.md"
        startTag="<<working-step-eight start>>"
        endTag="<<working-step-eight end>>"
        title="Guidance on working Step Eight"
      />

      {/* Video Modal for Step Eight */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="vX1pg5ql1lE"
        title="Step Eight Video Guide"
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={8}
        stepTitle="Step Eight"
        stepData={stepData}
        onStepUpdated={() => {
          setIsHistoryModalOpen(false);
          // Refresh step data
          if (user) {
            initializeProgress();
          }
        }}
      />

      {/* Step 8 List Modal */}
      <Step8ListModal
        isOpen={isListModalOpen}
        onClose={() => setIsListModalOpen(false)}
        user={user}
        onListUpdate={() => {
          loadListCount();
        }}
      />

    </div>
  );
};

export default Step8; 
